import React, { useRef, useState } from "react";
import "@/pages/toto-layered-slab-study.css";

const BODY_PATH = "M180 319 L205 319 L246 319 L276 311 L294 296 L299 268 L298 230 L303 201 L303 165 L315 146 L329 151 L345 184 L352 191 L362 184 L381 151 L394 154 L401 188 L418 204 L421 226 L448 237 L465 252 L472 270 L470 284 L460 296 L446 304 L427 303 L414 296 L402 307 L395 326 L389 337 L382 349 L378 373 L377 408 L378 449 L382 474 L391 485 L386 494 L370 497 L355 493 L349 484 L350 455 L351 420 L349 392 L342 377 L334 374 L330 394 L330 430 L330 466 L336 482 L330 492 L312 492 L301 486 L300 466 L301 444 L297 432 L282 434 L275 446 L275 469 L282 485 L276 492 L257 493 L247 486 L246 466 L247 448 L241 438 L225 425 L210 425 L202 443 L201 466 L194 483 L183 490 L164 490 L153 482 L155 464 L162 447 L166 429 L158 418 L154 394 L155 365 L159 343 L168 328 Z";
const TAIL_PATH = "M164 252 L155 258 L150 278 L153 299 L162 312 L174 321 L188 323 L194 318 L181 313 L171 302 L165 287 L165 272 L171 259 Z";
const COLLAR_PATH = "M293 286 L318 300 L350 314 L378 320 L399 316 L399 335 L377 340 L348 334 L316 320 L292 305 Z";
const MUZZLE_PATH = "M398 226 L421 228 L448 237 L465 252 L472 270 L470 284 L460 296 L446 304 L427 303 L414 296 L401 303 L389 296 L389 274 Z";
const CHEEK_PATH = "M333 196 C354 187 383 188 406 203 C416 211 421 220 421 229 L401 244 L382 246 L362 235 L344 219 Z";
const CHEST_PATH = "M358 325 C380 326 393 321 403 307 L395 326 L389 337 L382 349 L378 373 L377 408 L349 392 L342 377 L334 374 L340 344 Z";

function TotoSilhouette({ className = "" }) {
  return (
    <svg className={className} viewBox="142 138 338 367" aria-hidden="true">
      <path d={TAIL_PATH} className="toto-slab-tail" />
      <path d={BODY_PATH} className="toto-slab-body" />
      <circle cx="407" cy="224" r="5.1" className="toto-slab-eye" />
      <ellipse cx="457" cy="272" rx="7" ry="6" className="toto-slab-nose" />
    </svg>
  );
}

function TotoReliefDetails() {
  return (
    <svg className="toto-slab-relief" viewBox="142 138 338 367" aria-hidden="true">
      <path d={CHEEK_PATH} className="toto-slab-relief-cheek" />
      <path d={MUZZLE_PATH} className="toto-slab-relief-muzzle" />
      <path d={CHEST_PATH} className="toto-slab-relief-chest" />
      <path d="M315 151 L345 184 L352 191 L329 182 Z" className="toto-slab-relief-ear" />
      <path d="M381 151 L394 154 L401 188 L379 181 Z" className="toto-slab-relief-ear" />
      <circle cx="407" cy="224" r="5.1" className="toto-slab-eye" />
      <ellipse cx="457" cy="272" rx="7" ry="6" className="toto-slab-nose" />
    </svg>
  );
}

export default function TotoLayeredSlabStudy({ teamRgb = "92 134 112", teamName = "Team Green" }) {
  const slabRef = useRef(null);
  const [separated, setSeparated] = useState(false);

  const move = (event) => {
    const node = slabRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
    const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
    node.style.setProperty("--look-x", x.toFixed(3));
    node.style.setProperty("--look-y", y.toFixed(3));
  };

  const reset = () => {
    const node = slabRef.current;
    if (!node) return;
    node.style.setProperty("--look-x", "0");
    node.style.setProperty("--look-y", "0");
  };

  return (
    <main className="toto-slab-study" style={{ "--team-rgb": teamRgb }}>
      <header className="toto-slab-study__header">
        <div>
          <p>TOTO · EMPTY STATE · LAYERED GLASS STUDY</p>
          <h1>Toto in layers</h1>
        </div>
        <span>{teamName}</span>
      </header>

      <section className="toto-slab-study__stage">
        <button
          ref={slabRef}
          type="button"
          className={`toto-slab-icon ${separated ? "is-separated" : ""}`}
          onPointerMove={move}
          onPointerLeave={reset}
          onClick={() => setSeparated((value) => !value)}
          aria-pressed={separated}
          aria-label="Inspect Toto layered glass construction"
        >
          <span className="toto-slab-surface" aria-hidden="true" />

          <span className="toto-slab-dog toto-slab-dog--rear" aria-hidden="true"><TotoSilhouette /></span>
          <span className="toto-slab-dog toto-slab-dog--core" aria-hidden="true"><TotoSilhouette /></span>
          <span className="toto-slab-detail-layer" aria-hidden="true"><TotoReliefDetails /></span>

          <svg className="toto-slab-identity" viewBox="142 138 338 367" aria-hidden="true">
            <path d={COLLAR_PATH} className="toto-slab-collar" />
            <circle cx="378" cy="365" r="23" className="toto-slab-medal-back" />
            <circle cx="378" cy="365" r="18" className="toto-slab-medal" />
            <circle cx="372" cy="357" r="5.5" className="toto-slab-medal-highlight" />
          </svg>

          <span className="toto-slab-light" aria-hidden="true" />
        </button>
      </section>

      <footer className="toto-slab-study__footer">
        <p><strong>One backing slab:</strong> the vessel remains a single Liquid Glass surface.</p>
        <p><strong>Toto is laminated:</strong> rear silhouette, body core, facial relief and identity sit on distinct optical planes even at rest. <strong>Click:</strong> exaggerates the separation only for inspection.</p>
      </footer>
    </main>
  );
}
